import { useEffect, useRef, useState, useCallback } from "react";
import { realtimeClient } from "@/realtime/realtime-client";

interface UseRealtimeOptions {
  channel: string;
  onEvent?: (event: { type: string; payload: unknown; channel: string; timestamp: string }) => void;
  enabled?: boolean;
}

export function useRealtime({ channel, onEvent, enabled = true }: UseRealtimeOptions) {
  const [isConnected, setIsConnected] = useState(false);
  const [lastEvent, setLastEvent] = useState<{ type: string; payload: unknown; channel: string; timestamp: string } | null>(null);
  const onEventRef = useRef(onEvent);

  useEffect(() => {
    onEventRef.current = onEvent;
  }, [onEvent]);

  useEffect(() => {
    if (!enabled) return;

    realtimeClient.connect();

    const unsubscribe = realtimeClient.subscribe(channel, (event) => {
      setLastEvent(event);
      onEventRef.current?.(event);
    });

    const handleConnected = () => setIsConnected(true);
    const handleDisconnected = () => setIsConnected(false);

    realtimeClient.onConnected = handleConnected;
    realtimeClient.onDisconnected = handleDisconnected;

    return () => {
      unsubscribe();
    };
  }, [channel, enabled]);

  const publish = useCallback((event: { type: string; payload: unknown; timestamp: string }) => {
    realtimeClient.publish(channel, { ...event, channel });
  }, [channel]);

  return { isConnected, lastEvent, publish };
}
